import React, { useMemo, useState, useEffect, useCallback } from 'react'
import { Dialog } from '@headlessui/react'
import { useDispatch, useSelector } from 'react-redux'
import { AsyncSelect } from '@atlaskit/select'
import Button from '@atlaskit/button'
import Tooltip from '@atlaskit/tooltip'
import Avatar from '@atlaskit/avatar'
import CrossIcon from '@atlaskit/icon/glyph/cross'
import { token } from '@atlaskit/tokens'
import { updateTestRunAsync, selectTestRuns } from '../../store/slices/testScenarioSlice'
import { searchJiraUsers } from '../../utils/jiraContext'
import { useNotification } from '../../contexts/NotificationContext'
import { InlineEditField } from '../common/InlineEditField'

/**
 * EDIT RUN MODAL
 *
 * Modal for editing field values of an existing test run using Jira-style inline edit.
 * All fields save immediately when changed (no Save button).
 *
 * Field Types:
 * - TEXT: Renders as InlineEditField, saves immediately on confirm
 * - USER: Renders as AsyncSelect user picker, saves immediately on selection
 * - TIMESTAMP: Not shown (not editable by user)
 *
 * @param {boolean} isOpen - Whether modal is open
 * @param {function} onClose - Callback to close modal
 * @param {number} testScenarioId - ID of test scenario
 * @param {object} run - The test run object to edit
 * @param {Array} customFields - Array of custom field definitions from API
 * @param {object} preloadedUserData - Pre-fetched user data for USER fields (keyed by fieldName)
 */
export function EditRunModal({ isOpen, onClose, testScenarioId, run, customFields, preloadedUserData }) {
  const dispatch = useDispatch()
  const { showError } = useNotification()

  // Get live run data from Redux instead of relying on the stale `run` prop snapshot.
  // The `run` prop is only used as an ID reference to know WHICH run to edit.
  // This ensures field values always reflect the latest Redux state after dispatches.
  const testRuns = useSelector(selectTestRuns)
  const liveRun = useMemo(() => {
    if (!run?.id) return null
    return testRuns.find(r => r.id === run.id || r.id === Number(run.id))
  }, [testRuns, run?.id])

  // Portal target for dropdown menus to render outside modal
  const portalTarget = useMemo(
    () => (typeof document !== 'undefined' ? document.body : null),
    []
  )

  // Styles for AsyncSelect menu portal (higher z-index for modal)
  const selectStyles = useMemo(
    () => ({
      menuPortal: (base) => ({ ...base, zIndex: 2000 }),
    }),
    []
  )

  // Local state for USER field values - keyed by fieldName
  // TEXT fields don't need local state since InlineEditField handles it
  const [userFieldValues, setUserFieldValues] = useState({})

  // Filter and sort fields: only show editable fields (not TIMESTAMP), ordered by orderIndex
  const editableFields = useMemo(() => {
    if (!customFields) return []
    return customFields
      .filter(field => field.showField && field.fieldType !== 'TIMESTAMP')
      .sort((a, b) => a.orderIndex - b.orderIndex)
  }, [customFields])

  // Initialize USER field values when the modal opens (or switches to a different run).
  // This is an open-time initializer only — not a live sync hook. Deps intentionally
  // exclude `liveRun`, `editableFields`, and `preloadedUserData` so the effect does NOT
  // re-fire after a successful save (which would otherwise overwrite the freshly selected
  // option with stale pre-loaded user data and cause the dialog to revert the assignee).
  useEffect(() => {
    if (isOpen && run) {
      const initialUserValues = {}

      // Process only USER fields
      for (const field of editableFields) {
        if (field.fieldType === 'USER') {
          const existingValue = run?.[field.fieldName]

          if (existingValue && preloadedUserData && preloadedUserData[field.fieldName]) {
            // Use pre-loaded data from parent component
            initialUserValues[field.fieldName] = preloadedUserData[field.fieldName]
          } else {
            initialUserValues[field.fieldName] = null
          }
        }
      }

      setUserFieldValues(initialUserValues)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, run?.id])

  // Unassigned option for USER fields
  const UNASSIGNED_OPTION = {
    label: 'Unassigned',
    value: null,
    avatarUrl: null,
    isUnassigned: true
  }

  /**
   * LOAD USER OPTIONS
   * Async function to fetch Jira users based on search query.
   * Includes "Unassigned" option at the top.
   */
  const loadUserOptions = useCallback(async (inputValue) => {
    const users = await searchJiraUsers(inputValue)
    const userOptions = users.map(user => ({
      label: user.displayName,
      value: user.accountId,
      avatarUrl: user.avatarUrl
    }))
    return [UNASSIGNED_OPTION, ...userOptions]
  }, [])

  /**
   * FORMAT USER OPTION
   * Custom render for user options showing avatar + name
   */
  const formatUserOption = useCallback(({ label, avatarUrl, isUnassigned }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      {isUnassigned ? (
        <Avatar size="small" name="?" />
      ) : (
        <Avatar size="small" src={avatarUrl} name={label} />
      )}
      <span style={isUnassigned ? { color: token('color.text.subtle'), fontStyle: 'italic' } : undefined}>
        {label}
      </span>
    </div>
  ), [])

  /**
   * HANDLE TEXT FIELD CONFIRM
   * Saves a single TEXT field immediately when user confirms edit.
   */
  const handleTextFieldConfirm = useCallback(async (fieldName, newValue) => {
    if (!testScenarioId || !run?.id) return

    try {
      await dispatch(updateTestRunAsync({
        testScenarioId,
        testRunId: run.id,
        updates: { [fieldName]: newValue || '' }
      })).unwrap()
    } catch (error) {
      console.error(`Failed to update ${fieldName}:`, error)
      showError('Update Failed', `Failed to update ${fieldName}`)
      throw error
    }
  }, [dispatch, testScenarioId, run, showError])

  /**
   * HANDLE USER FIELD CHANGE
   * Saves a USER field immediately when selection changes.
   */
  const handleUserFieldChange = useCallback(async (fieldName, selectedOption) => {
    if (!testScenarioId || !run?.id) return

    // Update local state for immediate UI feedback
    setUserFieldValues(prev => ({
      ...prev,
      [fieldName]: selectedOption
    }))

    // Determine the value to send to API (accountId or null)
    const valueForApi = selectedOption?.value || null

    try {
      await dispatch(updateTestRunAsync({
        testScenarioId,
        testRunId: run.id,
        updates: { [fieldName]: valueForApi }
      })).unwrap()
    } catch (error) {
      console.error(`Failed to update ${fieldName}:`, error)
      showError('Update Failed', `Failed to update ${fieldName}`)
      // Revert on error
      setUserFieldValues(prev => ({
        ...prev,
        [fieldName]: preloadedUserData?.[fieldName] || null
      }))
    }
  }, [dispatch, testScenarioId, run, showError, preloadedUserData])

  /**
   * RENDER FIELD
   * Renders the appropriate component based on fieldType
   */
  const renderField = (field) => {
    switch (field.fieldType) {
      case 'TEXT':
        return (
          <InlineEditField
            key={field.id}
            label={field.fieldName}
            value={(liveRun || run)?.[field.fieldName] || ''}
            onConfirm={(newValue) => handleTextFieldConfirm(field.fieldName, newValue)}
            placeholder={`Enter ${field.fieldName}`}
          />
        )

      case 'USER':
        return (
          <div key={field.id} style={{ marginBottom: '20px' }}>
            <label
              style={{
                display: 'block',
                fontSize: '12px',
                fontWeight: 600,
                color: token('color.text.subtle', '#5E6C84'),
                marginBottom: '8px'
              }}
            >
              {field.fieldName}
            </label>
            <AsyncSelect
              cacheOptions
              defaultOptions={[UNASSIGNED_OPTION]}
              loadOptions={loadUserOptions}
              value={userFieldValues[field.fieldName] || null}
              onChange={(option) => handleUserFieldChange(field.fieldName, option)}
              placeholder="Search for a user..."
              isClearable
              formatOptionLabel={formatUserOption}
              menuPortalTarget={portalTarget}
              menuPlacement="auto"
              styles={selectStyles}
              noOptionsMessage={({ inputValue }) =>
                inputValue ? `No users found for "${inputValue}"` : 'Start typing to search users'
              }
            />
          </div>
        )

      default:
        return null
    }
  }

  // Get run name for title (use first TEXT field value or "Run")
  // Uses liveRun for live updates when fields are edited while modal is open
  const runName = useMemo(() => {
    const activeRun = liveRun || run
    if (!activeRun || !customFields) return 'Run'
    const nameField = customFields.find(f => f.fieldName === 'Run Name' || f.fieldName === 'Name')
    if (nameField && activeRun[nameField.fieldName]) {
      return activeRun[nameField.fieldName]
    }
    return 'Run'
  }, [liveRun, run, customFields])

  return (
    <Dialog open={isOpen} onClose={onClose} className="headless-dialog-root">
      {/* Dark semi-transparent backdrop overlay - matches Jira style */}
      <div
        className="headless-dialog-backdrop"
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'var(--ds-blanket)',
          zIndex: 1000
        }}
        aria-hidden="true"
      />

      {/* Modal Container - centered with flexbox */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
          pointerEvents: 'none'
        }}
      >
        <Dialog.Panel
          key={`edit-run-modal-${run?.id}`}
          style={{
            backgroundColor: 'var(--ds-surface-overlay, #FFFFFF)',
            borderRadius: '8px',
            boxShadow: '0 8px 16px -4px rgba(9, 30, 66, 0.25), 0 0 1px rgba(9, 30, 66, 0.31)',
            width: '500px',
            maxHeight: '80vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            pointerEvents: 'auto'
          }}
        >
          {/* JIRA-STYLE HEADER WITH CLOSE BUTTON */}
          <div
            style={{
              padding: '16px 24px',
              borderBottom: '1px solid var(--ds-border, #DFE1E6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0
            }}
          >
            {/* Left: Title */}
            <Dialog.Title
              style={{
                fontSize: '16px',
                fontWeight: 500,
                color: 'var(--ds-text, #172B4D)',
                margin: 0
              }}
            >
              Edit {runName}
            </Dialog.Title>

            {/* Right: Close button */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Tooltip content="Close">
                <Button
                  appearance="subtle"
                  iconBefore={<CrossIcon label="Close" size="medium" />}
                  onClick={onClose}
                />
              </Tooltip>
            </div>
          </div>

          {/* BODY */}
          <div
            style={{
              padding: '20px 24px',
              flex: 1,
              overflowY: 'auto',
              minHeight: editableFields.length > 0 ? `${editableFields.length * 80}px` : '100px'
            }}
          >
            {editableFields.length === 0 ? (
              <p style={{ color: token('color.text.subtle'), fontStyle: 'italic' }}>
                No editable fields available.
              </p>
            ) : (
              editableFields.map(field => renderField(field))
            )}
          </div>

          {/* FOOTER - Single Close button */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              padding: '16px 24px',
              borderTop: '1px solid var(--ds-border, #DFE1E6)',
              flexShrink: 0
            }}
          >
            <Button
              appearance="primary"
              onClick={onClose}
            >
              Close
            </Button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  )
}
